import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EMPTY_RESEARCH_TOOL,
  RESEARCH_TOOL_TYPES,
  validateResearchTool,
} from '../../lib/researchTools.js';
import { findSimilarResearchTools } from '../../lib/findSimilar.js';
import { useSimilarCheck } from '../../hooks/useSimilarCheck.js';
import SimilarEntriesWarning from '../common/SimilarEntriesWarning.jsx';
import AnnounceCheckbox from '../common/AnnounceCheckbox.jsx';

export default function ResearchToolForm({
  initialValue,
  submitLabel,
  onSubmit,
  onCancel,
  showAnnounce = false,
  busy = false,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({ ...EMPTY_RESEARCH_TOOL, ...initialValue }));
  const [error, setError] = useState(null);

  // Reset only when the edited entry actually changes (different id), not
  // on every new prop reference — see comment in ScholarForm.jsx.
  useEffect(() => {
    if (initialValue) setForm({ ...EMPTY_RESEARCH_TOOL, ...initialValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue?.id]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  const { candidates: similarCandidates } = useSimilarCheck(
    { name: form.name, url: form.url, excludeId: initialValue?.id },
    findSimilarResearchTools,
  );

  async function handleSubmit(event) {
    event.preventDefault();
    const errKey = validateResearchTool(form);
    if (errKey) {
      setError(t(errKey));
      return;
    }
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <Field label={t('researchTools.form.type')} required>
        <div className="flex flex-wrap gap-2">
          {RESEARCH_TOOL_TYPES.map((type) => (
            <label
              key={type}
              className={`cursor-pointer rounded-full border px-4 py-1.5 text-sm transition ${
                form.type === type
                  ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
                  : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
              }`}
            >
              <input
                type="radio"
                name="research-tool-type"
                value={type}
                checked={form.type === type}
                onChange={(e) => update({ type: e.target.value })}
                className="sr-only"
              />
              {t(`researchTools.types.${type}`)}
            </label>
          ))}
        </div>
      </Field>

      <Field label={t('researchTools.form.name')} required>
        <Input value={form.name} onChange={(v) => update({ name: v })} />
      </Field>

      <Field label={t('researchTools.form.url')} hint={t('researchTools.form.urlHint')} required>
        <Input type="text" inputMode="url" value={form.url} onChange={(v) => update({ url: v })} placeholder="https://…" />
      </Field>

      <Field label={t('researchTools.form.institution')}>
        <Input value={form.institution} onChange={(v) => update({ institution: v })} />
      </Field>

      <Field label={t('researchTools.form.description')} hint={t('researchTools.form.descriptionHint')}>
        <Textarea rows={5} value={form.description} onChange={(v) => update({ description: v })} />
      </Field>

      {showAnnounce && (
        <AnnounceCheckbox
          checked={form.announce_in_whats_new}
          onChange={(v) => update({ announce_in_whats_new: v })}
        />
      )}

      <SimilarEntriesWarning candidates={similarCandidates} kind="research_tools" />

      {error && (
        <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.loading') : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
        {required && <span className="ml-1 text-gold">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      {...rest}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}

function Textarea({ value, onChange, rows = 4, ...rest }) {
  return (
    <textarea
      {...rest}
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}
